import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EMPTY_STRING, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    AccountMappingTemplateGroupItem,
    AccountType,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    IReclassesAccounts,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { difference } from 'lodash-es';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DISPLAY_TYPES } from '../../../constants/adjustments-and-reclasses/displayTypes.constant';
import { BorderColor } from '../../../enums/adjustments-and-reclasses/BorderColor.enum';
import { MappingDisplayItemTypes } from '../../../enums/adjustments-and-reclasses/MappingDisplayItemTypes.enum';
import { IAdjustmentAndReclassesControlAddition } from '../../../interfaces/adjustments-and-reclasses/IAdjustmentAndReclassesControlAddition.interface';
import { AccountMappingEngine } from '../../../mappers/account-mapping-engin';
import { CellDisplayType } from '../../../types/adjustments-and-reclasses/CellDisplayType.type';
import { CreateReclassesHandler } from '../../../types/adjustments-and-reclasses/CreateReclassesHandler.type';
import { MappingDisplayItem } from '../../../types/adjustments-and-reclasses/MappingDisplayItem.type';
import { MappingDisplayItemWithForm } from '../../../types/adjustments-and-reclasses/MappingDisplayItemWithForm.type';
import { MappingDisplayItemWithNoForm } from '../../../types/adjustments-and-reclasses/MappingDisplayItemWithNoForm.type';
import { RemoveReclassesHandler } from '../../../types/adjustments-and-reclasses/RemoveReclassesHandler.type';
import { AdjustmentAndReclassesUIHelper } from './adjustmentAndReclassesUI.helper';
import { AdjustmentAndReclassesController } from './adjustmentandReclassesController';

const debug = Debug('aumico:project-reports:adjustments-reclasses-group-form.component');

const groupType = '-group';
const itemType = '-item';

/**
 * Data passed to the component will be mutated
 */
@UntilDestroy()
@Component({
    selector: 'adjustments-reclasses-group-form-component',
    templateUrl: 'adjustments-reclasses-group-form.component.html',
    styleUrls: ['adjustments-reclasses-group-form.component.scss'],
})
export class AdjustmentsReclassesGroupFormComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private triggerUpdate$ = new Subject<void>();
    private adjustmentController: AdjustmentAndReclassesController;
    private adjustmentHelper: AdjustmentAndReclassesUIHelper;

    collapsedRowIdToChildrenId: Map<string, string[]> = new Map<string, string[]>();
    private hiddenRows = new Set<string>();
    private templateGroupLevelsIdToLevelChildrenId: Map<string, string[]> = new Map<string, string[]>();
    currentActiveRowIndex: number;

    displayTypes = DISPLAY_TYPES;
    private isTemplateGroupRowIdsToRowChildrenIdsReady = false;
    private _language = 'de-ch';
    headingNode: { class: string; cells: CellDisplayType[] };
    public get language() {
        return this._language;
    }
    @Input()
    public set language(value: string) {
        this._language = value;
    }
    @Input() defaultLanguage = 'de-ch';

    private _templateGroup: IAccountMappingTemplateGroup;
    public get templateGroup(): IAccountMappingTemplateGroup {
        return this._templateGroup;
    }
    @Input()
    public set templateGroup(value: IAccountMappingTemplateGroup) {
        // template can't be cloned deep here it must be cloned before the engine is created be called
        this._templateGroup = value;
    }

    private _engine: AccountMappingEngine;
    @Input()
    public get engine(): AccountMappingEngine {
        return this._engine;
    }
    public set engine(value: AccountMappingEngine) {
        debug('######### new child set engine');
        this._engine = value;
    }

    @Input() set shouldRecalculate(value: boolean) {
        if (value) {
            this.updateDisplayItems();
        }
    }

    /**
     * REV-XXXXX: when true, reclass cells render in read-only state in
     * the legacy reclasses tab. The consuming page derives this from
     * `isReclassNewWorld(ar)` (new-world reports). The flag is
     * propagated into the helper instance and a re-render is triggered
     * so existing display items pick up the new editable state.
     */
    @Input() set readonlyReclasses(value: boolean) {
        const next = value === true;
        if (this.adjustmentHelper != null && this.adjustmentHelper.readonlyReclasses === next) return;
        if (this.adjustmentHelper != null) {
            this.adjustmentHelper.readonlyReclasses = next;
            this.updateDisplayItems();
        }
    }

    @Input()
    currency: string;

    @Input()
    wordBeforeCurrency = 'in';

    private _totalWord = 'Total';
    public get totalWord() {
        return this._totalWord;
    }
    @Input()
    public set totalWord(value) {
        this._totalWord = value;
        // this.initializeDisplayedItems();
    }

    // refer to finNumber pipe for reference on values
    @Input()
    numberFormat = '1.2-2';

    // TODO: Rename the variable
    private _templateRowIdToRowChildrenIds: Map<string, string[]>;
    @Input() set templateRowIdToRowChildrenIds(value: Map<string, string[]>) {
        if (value != null) {
            this._templateRowIdToRowChildrenIds = value;
        }
    }
    private _templateRowIdToParentsIds: Map<string, Set<string>>;
    @Input() set templateRowIdToParentsIds(value: Map<string, Set<string>>) {
        if (value != null) {
            this._templateRowIdToParentsIds = value;
        }
    }

    get dates(): {
        startDate: string | undefined;
        endDate: string | undefined;
    } {
        if (this.engine) {
            const dates = this.engine.getDates();
            return dates;
        }
        return {
            startDate: undefined,
            endDate: undefined,
        };
    }

    @Output()
    copiedBalanceChangeInAdjustmentsToIncomeAdjustment: EventEmitter<number | null> = new EventEmitter<number | null>();
    displayItems: MappingDisplayItem[] = [];

    constructor() {
        this.triggerUpdate$
            .pipe(
                filter(() => this.templateGroup && this.engine != null),
                untilDestroyed(this),
            )
            .subscribe(() => {
                debug('calling(_updateDisplayItems)');
                this.updateDisplayItems();
            });
        this.adjustmentHelper = new AdjustmentAndReclassesUIHelper();
        this.adjustmentController = new AdjustmentAndReclassesController(
            () => this.engine,
            () => this.reassignDisplayItems(),
            () => this.destroy$,
            () => this.adjustmentHelper,
        );
    }

    ngOnInit(): void {
        this.initializeDisplayedItems();
    }

    private initializeDisplayedItems() {
        if (
            this.language != null &&
            this.templateGroup != null &&
            !this.templateGroup.isEmpty &&
            !this.templateGroup.isHeader &&
            this.engine != null &&
            this.totalWord != null &&
            this._templateRowIdToRowChildrenIds != null &&
            this._templateRowIdToParentsIds != null
        ) {
            this.updateDisplayItems();
        }
    }

    clickUtil(e: Event, doAfter: () => void) {
        this.adjustmentHelper.clickUtil(e, doAfter);
    }
    /**
     * should be updated when updating
     * this.totalWord
     * this.templateGroup
     * this.language
     * this.isHideZeroFLIs
     */
    triggerUpdateDisplayItemsDebounced() {
        this.triggerUpdate$.next();
    }
    updateDisplayItems() {
        const level = 0;
        let [showChange, showIncome] = [false, false];

        const displayItems: MappingDisplayItem[] = [];
        this.displayItems = displayItems;
        if (this.templateGroup != null && this.engine != null) {
            this.destroySubscriptions();
            if (this.engine.getType() === TemplateType.BALANCE) {
                [showChange, showIncome] = [true, true];
            }
            // destroy previous subscriptions if we want to rebuild all
            this.headingNode = this.adjustmentHelper.getHeadingNode({
                title: (this.templateGroup.title ?? EMPTY_STRING) + ' ' + this.wordBeforeCurrency + ' ' + this.currency,
                dateEnd: this.dates.endDate,
                dateStart: this.dates.startDate,
                showChange,
                showIncome,
            });
            this.engine.calculateAll(true);
            this.processGroup({
                templateGroup: this.templateGroup,
                displayItems,
                level,
            });
        }
        displayItems.map((item) => this.adjustmentHelper.prepareForDisplay(item, showChange, showIncome));
        debug('########## ITEMS SHOULD DISPLAY #######');
        debug(this.displayItems.length);
        this.isTemplateGroupRowIdsToRowChildrenIdsReady = true;
    }

    processGroup({
        templateGroup,
        displayItems,
        level,
    }: {
        templateGroup: IAccountMappingTemplateGroup;
        displayItems: MappingDisplayItem[];
        level: number;
    }) {
        const adjustmentHelper = this.adjustmentHelper;
        if (!this.isTemplateGroupRowIdsToRowChildrenIdsReady && this._templateRowIdToRowChildrenIds) {
            const childrenIds = this._templateRowIdToRowChildrenIds.get(`${templateGroup.name}${groupType}`);
            this.templateGroupLevelsIdToLevelChildrenId.set(`${templateGroup.name}${groupType}`, childrenIds);
        }

        if (!templateGroup.isEmpty && !templateGroup.isHeader) {
            displayItems.push({
                bold: true,
                borderDown: BorderColor.BLACK,
                title: this.totalWord + ' ' + (templateGroup.totalName ?? EMPTY_STRING),
                type: MappingDisplayItemTypes.GROUP_TOTAL,
                level,
                ...this.getCommonDisplayProperties(templateGroup),
                getLatest() {
                    // this is referring to the object here
                    adjustmentHelper.updateCalculations(this, templateGroup);
                },
                id: `${templateGroup.name}${groupType}`,
                childrenIds: this.templateGroupLevelsIdToLevelChildrenId.get(`${templateGroup.name}${groupType}`),
                isCollapsed:
                    this.collapsedRowIdToChildrenId.get(`${templateGroup.name}${groupType}`) != null ? true : false,
                isRowActive: true,
                isHidden: this.hiddenRows.has(`${templateGroup.name}${groupType}`),
            });
        }

        // resolve inner groups
        for (let index = 0; index < (templateGroup.groups || []).length; index++) {
            this.processGroup({
                templateGroup: templateGroup.groups[index],
                displayItems,
                level: level + 1,
            });
        }
        // resolve inner items
        this.processGroupFLIs({
            items: templateGroup.items || [],
            displayItems,
            level,
        });
    }

    private getCommonDisplayProperties(
        templateGroup:
            | IAccountMappingTemplateGroup
            | IAccount
            | IReclassesAccounts
            | IAccountMappingTemplateGroupItemFLI
            | IAccountMappingSubFLI
            | IAccountMappingTemplateGroupItemSubtotal,
    ): {
        paddingTop: boolean;
        closing: number;
        opening: number;
        closingReClass: number | undefined;
        openingReClass: number | undefined;
        closingAdjustment: number | undefined;
        openingAdjustment: number | undefined;
    } {
        let closingReClass: number | undefined;
        if (templateGroup.closingReClass != null && NumberUtils.isFinite(templateGroup.closingReClass)) {
            closingReClass = NumberUtils.roundNumber(templateGroup.closingReClass);
        }

        let openingReClass: number | undefined;
        if (templateGroup.openingReClass != null && NumberUtils.isFinite(templateGroup.openingReClass)) {
            openingReClass = NumberUtils.roundNumber(templateGroup.openingReClass);
        }

        let closingAdjustment: number | undefined;
        if (templateGroup.closingAdjustment != null && NumberUtils.isFinite(templateGroup.closingAdjustment)) {
            closingAdjustment = NumberUtils.roundNumber(templateGroup.closingAdjustment);
        }

        let openingAdjustment: number | undefined;
        if (templateGroup.openingAdjustment != null && NumberUtils.isFinite(templateGroup.openingAdjustment)) {
            openingAdjustment = NumberUtils.roundNumber(templateGroup.openingAdjustment);
        }

        let closingValue = 0;
        if (
            templateGroup.calculatedClosingBalance != null &&
            NumberUtils.isFinite(templateGroup.calculatedClosingBalance)
        ) {
            closingValue = NumberUtils.roundNumber(templateGroup.calculatedClosingBalance);
        }

        let openingValue = 0;
        if (
            templateGroup.calculatedOpeningBalance != null &&
            NumberUtils.isFinite(templateGroup.calculatedOpeningBalance)
        ) {
            openingValue = NumberUtils.roundNumber(templateGroup.calculatedOpeningBalance);
        }
        return {
            paddingTop: false,
            closing: closingValue,
            opening: openingValue,
            closingReClass: closingReClass,
            openingReClass: openingReClass,
            closingAdjustment: closingAdjustment,
            openingAdjustment: openingAdjustment,
        };
    }

    processGroupFLIs({
        items,
        displayItems,
        level,
    }: {
        items: AccountMappingTemplateGroupItem[];
        displayItems: MappingDisplayItem[];
        level: number;
    }) {
        let itemsProcessed = false;
        for (let index = 0; index < (items || []).length; index++) {
            if (items[index].type === ItemType.HEADER || items[index].type === ItemType.EMPTY) {
                continue;
            }

            itemsProcessed = true;
            const fli = items[index];
            if (fli.type === ItemType.SUB_TOTAL) {
                // in case of subtotal, make the previous tile boarder black
                this.processSubtotal({
                    fli,
                    displayItems,
                    level: level + 1,
                });
            } else if (fli.type === ItemType.FLI) {
                this.processOneGroupFli({
                    fli,
                    displayItems,
                    level: level + 1,
                });
            }
        }
        // make last item with black border
        if (itemsProcessed) {
            displayItems[displayItems.length - 1].borderDown = BorderColor.BLACK;
        }
    }

    doAfterReclassesAddRemove() {
        this.engine.calculateAll(true);
        this.triggerUpdateDisplayItemsDebounced();
    }
    private processOneGroupFli({
        fli,
        displayItems,
        level,
    }: {
        fli: IAccountMappingTemplateGroupItemFLI;
        displayItems: MappingDisplayItem[];
        level: number;
    }) {
        fli.accounts = fli.accounts || [];

        const reclassesCreationHandler = this.adjustmentHelper.createReclassesCreationHandler(fli, () => {
            const lastReclassesCreatedAccount = fli.reclassesAccounts.slice(-1)[0];

            this.addReclassesAccountIdToUpperParentsLevels(
                `${fli.name}${itemType}`,
                lastReclassesCreatedAccount.accountNumber,
            );
            this.doAfterReclassesAddRemove();
        });
        displayItems.push(
            this.createFliDisplayItem({
                fli,
                displayType: MappingDisplayItemTypes.FLI,
                level,
                reclassesCreationHandler,
            }),
        );
        this.processAccounts(fli, displayItems, level);
    }

    private addReclassesAccountIdToUpperParentsLevels(fliLevelId: string, childIdToAdd: string): void {
        const parentLevelsIds = this._templateRowIdToParentsIds.get(fliLevelId);
        parentLevelsIds.add(fliLevelId);
        for (const levelId of parentLevelsIds) {
            const fliChildrenIds = this.templateGroupLevelsIdToLevelChildrenId.get(levelId);
            if (fliChildrenIds != null) {
                fliChildrenIds.push(childIdToAdd);
                this.templateGroupLevelsIdToLevelChildrenId.set(levelId, fliChildrenIds);
            }
        }
    }

    processAccount({
        account,
        displayItems,
        level,
    }: {
        account: IAccount;
        displayItems: MappingDisplayItem[];
        level: number;
    }) {
        const adjustmentHelper = this.adjustmentHelper;

        displayItems.push({
            bold: false,
            borderDown: BorderColor.GRAY,
            title: account.name,
            accountNumber: account.accountNumber,
            type: MappingDisplayItemTypes.ACCOUNT,
            isSubFLI: false,
            level,
            ...this.getCommonDisplayProperties(account),
            ...this.adjustmentController.getAdditionControls(account),
            account: account,
            getLatest() {
                adjustmentHelper.updateCalculations(this, account);
            },
            id: account.accountNumber,
            isRowActive: false,
            isHidden: this.hiddenRows.has(account.accountNumber),
        });
    }

    processReclassesAccount({
        reclassesAccount,
        displayItems,
        level,
        removeReclassesHandler,
    }: {
        reclassesAccount: IReclassesAccounts;
        displayItems: MappingDisplayItem[];
        level: number;
        removeReclassesHandler: RemoveReclassesHandler;
    }) {
        const adjustmentHelper = this.adjustmentHelper;

        const displayItem: MappingDisplayItem & IAdjustmentAndReclassesControlAddition = {
            bold: false,
            borderDown: BorderColor.GRAY,
            title: reclassesAccount.name,
            accountNumber: reclassesAccount.accountNumber,
            type: MappingDisplayItemTypes.RECLASSES,
            isSubFLI: false,
            level,
            ...this.getCommonDisplayProperties(reclassesAccount),
            ...this.adjustmentController.getAdditionControls(reclassesAccount),
            account: reclassesAccount,
            getLatest() {
                adjustmentHelper.updateCalculations(this, reclassesAccount);
            },
            removeReclassesAccount: removeReclassesHandler,
            id: reclassesAccount.accountNumber,
            isRowActive: false,
            isHidden: this.hiddenRows.has(reclassesAccount.accountNumber),
        };
        displayItems.push(displayItem);
    }

    reassignDisplayItems() {
        (this.displayItems || []).forEach((item) => {
            item.getLatest();
        });
    }

    private processSubFli({
        sub,
        displayItems,
        level,
    }: {
        sub: IAccountMappingSubFLI;
        displayItems: MappingDisplayItem[];
        level: number;
    }) {
        sub.accounts = sub.accounts || [];
        const reclassesCreationHandler = this.adjustmentHelper.createReclassesCreationHandler(sub, () => {
            const lastReclassesCreatedAccount = sub.reclassesAccounts.slice(-1)[0];
            this.addReclassesAccountIdToUpperParentsLevels(
                `${sub.name}${itemType}`,
                lastReclassesCreatedAccount.accountNumber,
            );
            this.doAfterReclassesAddRemove();
        });

        displayItems.push(
            this.createFliDisplayItem({
                fli: sub,
                displayType: MappingDisplayItemTypes.SUB_FLI,
                level,
                reclassesCreationHandler,
            }),
        );
        this.processAccounts(sub, displayItems, level);
    }

    private processAccounts(
        fli: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        displayItems: MappingDisplayItem[],
        level: number,
    ) {
        for (const item of fli.accounts) {
            if (item.type === ItemType.HEADER || item.type === ItemType.EMPTY) {
                continue;
            }

            if (item.type === ItemType.SUB_FLI || item.type === ItemType.OF_THAT_ITEM) {
                this.processSubFli({
                    sub: item,
                    displayItems,
                    level: level + 1,
                });
            } else {
                this.processAccount({
                    account: item as IAccount,
                    displayItems,
                    level,
                });
            }
        }

        for (const [index, reclassesAccount] of fli.reclassesAccounts.entries()) {
            if (reclassesAccount.type === AccountType.RECLASSES) {
                const removeReclassesHandler = this.adjustmentHelper.createReclassesRemoveHandlers(
                    fli,
                    index,
                    (removedReclassesAccountId) => {
                        this.removeReclassesAccountIdFromUpperParentsLevels(
                            `${fli.name}${itemType}`,
                            removedReclassesAccountId,
                        );
                        this.doAfterReclassesAddRemove();
                        this.currentActiveRowIndex = null;
                    },
                );
                this.processReclassesAccount({
                    reclassesAccount,
                    displayItems,
                    level,
                    removeReclassesHandler,
                });
            }
        }
    }

    private removeReclassesAccountIdFromUpperParentsLevels(fliLevelId: string, childIdToBeRemoved: string): void {
        const parentLevelsId = this._templateRowIdToParentsIds.get(fliLevelId);
        parentLevelsId.add(fliLevelId);
        for (const levelId of parentLevelsId) {
            let fliChildrenIds = this.templateGroupLevelsIdToLevelChildrenId.get(levelId);
            if (fliChildrenIds != null) {
                fliChildrenIds = difference<string>(fliChildrenIds, [childIdToBeRemoved]);
                this.templateGroupLevelsIdToLevelChildrenId.set(levelId, fliChildrenIds);
            }
        }
    }

    private createFliDisplayItem({
        fli,
        displayType,
        level,
        reclassesCreationHandler,
    }: {
        fli: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI;
        displayType: typeof MappingDisplayItemTypes.FLI | typeof MappingDisplayItemTypes.SUB_FLI;
        level: number;
        reclassesCreationHandler: CreateReclassesHandler;
    }): MappingDisplayItemWithNoForm {
        const adjustmentHelper = this.adjustmentHelper;
        if (!this.isTemplateGroupRowIdsToRowChildrenIdsReady && this._templateRowIdToRowChildrenIds) {
            const childrenIds = this._templateRowIdToRowChildrenIds.get(`${fli.name}${itemType}`);
            this.templateGroupLevelsIdToLevelChildrenId.set(`${fli.name}${itemType}`, childrenIds);
        }

        return {
            bold: false,
            borderDown: BorderColor.GRAY,
            title: fli.caption,
            type: displayType,
            isSubFLI: displayType === MappingDisplayItemTypes.SUB_FLI,
            level,
            ...this.getCommonDisplayProperties(fli),
            getLatest() {
                adjustmentHelper.updateCalculations(this, fli);
            },
            createReclassesAccount: reclassesCreationHandler,
            id: `${fli.name}${itemType}`,
            childrenIds: this.templateGroupLevelsIdToLevelChildrenId.get(`${fli.name}${itemType}`),
            isCollapsed: this.collapsedRowIdToChildrenId.get(`${fli.name}${itemType}`) != null ? true : false,
            isRowActive: true,
            isHidden: this.hiddenRows.has(`${fli.name}${itemType}`),
        };
    }

    private processSubtotal({
        fli,
        displayItems,
        level,
    }: {
        fli: IAccountMappingTemplateGroupItemSubtotal;
        displayItems: MappingDisplayItem[];
        level: number;
    }) {
        const adjustmentHelper = this.adjustmentHelper;
        if (!this.isTemplateGroupRowIdsToRowChildrenIdsReady && this._templateRowIdToRowChildrenIds) {
            const childrenIds = this._templateRowIdToRowChildrenIds.get(`${fli.name}${groupType}`);
            this.templateGroupLevelsIdToLevelChildrenId.set(`${fli.name}${groupType}`, childrenIds);
        }
        if (displayItems.length > 0) {
            displayItems[displayItems.length - 1].borderDown = BorderColor.BLACK;
        }
        displayItems.push({
            bold: true,
            borderDown: BorderColor.BLACK,
            title: fli.caption ?? EMPTY_STRING,
            type: MappingDisplayItemTypes.SUBTOTAL,
            level,
            ...this.getCommonDisplayProperties(fli),
            getLatest() {
                adjustmentHelper.updateCalculations(this, fli);
            },
            id: `${fli.name}${groupType}`,
            childrenIds: this.templateGroupLevelsIdToLevelChildrenId.get(`${fli.name}${groupType}`),
            isCollapsed: this.collapsedRowIdToChildrenId.get(`${fli.name}${groupType}`) != null ? true : false,
            isRowActive: true,
            isHidden: this.hiddenRows.has(`${fli.name}${groupType}`),
        });
    }

    private copyBalanceChangeInAdjustmentToIncomeAdjustment(value: number | null): void {
        let balanceChangeInAdjustment: number | null = null;
        if (value != null && NumberUtils.isFinite(value)) {
            balanceChangeInAdjustment = NumberUtils.roundNumber(value);
        }
        this.copiedBalanceChangeInAdjustmentsToIncomeAdjustment.emit(balanceChangeInAdjustment);
    }

    copyBalanceChangeInAdjustmentToIncomeAccountAdjustment(elementIndex: number): void {
        const changeInAdjustments = this.adjustmentHelper.calculateChangeInAdjustment(
            this.displayItems[elementIndex].closingAdjustment,
            this.displayItems[elementIndex].openingAdjustment,
        );

        this.copyBalanceChangeInAdjustmentToIncomeAdjustment(changeInAdjustments);
    }

    public collapseExpandChildrenOfRow(row: MappingDisplayItem): void {
        // setTimeout is used to make it async
        setTimeout(() => {
            const childrenIds = new Set(row.childrenIds);

            if (row.isCollapsed) {
                this.collapsedRowIdToChildrenId.delete(row.id);
            } else {
                this.collapsedRowIdToChildrenId.set(row.id, row.childrenIds);
            }

            row.isCollapsed = !row.isCollapsed;

            for (const node of this.displayItems) {
                const id = node.id;
                if (!childrenIds.has(id)) {
                    continue;
                }

                node.isHidden = row.isCollapsed;
                if (row.isCollapsed) {
                    this.hiddenRows.add(node.id);
                } else {
                    this.hiddenRows.delete(node.id);
                }

                if (node.isCollapsed != null) {
                    node.isCollapsed = row.isCollapsed;
                    node.isCollapsed
                        ? this.collapsedRowIdToChildrenId.set(node.id, node.childrenIds)
                        : this.collapsedRowIdToChildrenId.delete(node.id);
                }
            }
        }, 0);
    }

    public activateAccountRow(rowIndex: number): void {
        if (this.currentActiveRowIndex != null && this.currentActiveRowIndex === rowIndex) {
            return;
        } else if (this.currentActiveRowIndex == null) {
            this.detectChangesOfActivatedRow(rowIndex);
        } else if (this.currentActiveRowIndex != null && this.currentActiveRowIndex !== rowIndex) {
            this.displayItems[this.currentActiveRowIndex].isRowActive = false;
            this.detectChangesOfActivatedRow(rowIndex);
        }
    }

    private detectChangesOfActivatedRow(rowIndex: number): void {
        this.destroySubscriptions();
        this.currentActiveRowIndex = rowIndex;
        this.displayItems[rowIndex].isRowActive = true;

        const activatedRow = this.displayItems[rowIndex];
        const activatedRowWithForm = activatedRow as MappingDisplayItemWithForm;

        const formControls: IAdjustmentAndReclassesControlAddition = {
            openingReClassControl: activatedRowWithForm.openingReClassControl,
            openingAdjustmentControl: activatedRowWithForm.openingAdjustmentControl,
            closingReClassControl: activatedRowWithForm.closingReClassControl,
            closingAdjustmentControl: activatedRowWithForm.closingAdjustmentControl,
            nameControl: activatedRowWithForm.nameControl ?? null,
        };

        if (activatedRow.isAccount) {
            this.adjustmentController.detectAccountRowFormValueChanges(formControls, activatedRow.account);
        } else if (activatedRow.isReclasses) {
            this.adjustmentController.detectReclassesAccountRowFormValueChange(formControls, activatedRow.account);
        }
    }

    // used to destroy all current subscriptions to be able to cleanly recreate them without ghosts
    // subscriptions target by the destroy signal would use pipe(takeUntil(this.destroy$))
    destroySubscriptions() {
        this.destroy$.next();
    }

    ngOnDestroy() {
        this.destroySubscriptions();
        this.destroy$.complete();
    }
}
